// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useState } from "react"
import {
  generateMermaidFlowchart,
  isGateNodeType,
  INPUT_ID,
  OUTPUT_ID,
} from "@/lib/mermaid-generator"
import { MermaidDiagram } from "@/components/MermaidDiagram"
import { NodeInspector } from "@/components/NodeInspector"

type EndpointId = typeof INPUT_ID | typeof OUTPUT_ID
function endpointOf(id: string | null): EndpointId | null {
  return id === INPUT_ID || id === OUTPUT_ID ? (id as EndpointId) : null
}

// Degraded view: shown when the diagram cannot be rendered so a malformed
// evolved agent never leaves the user with a blank graph zone.
function GraphTextLists({ model }: { model: GraphAgentModel }) {
  return (
    <div className="space-y-3">
      <p role="status" className="text-sm text-expired">
        The diagram could not be rendered — showing the node/edge lists
        instead.
      </p>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-foreground">
          Nodes ({model.nodes.length})
        </h3>
        {model.nodes.length === 0 ? (
          <p className="text-sm text-text-tertiary">No nodes declared.</p>
        ) : (
          <ul className="space-y-1">
            {model.nodes.map((node) => (
              <li
                key={node.id}
                className="flex flex-row items-baseline gap-2 text-sm"
              >
                <span className="font-mono text-text-secondary">
                  {node.name}
                </span>
                <span className="text-xs text-text-tertiary">
                  ({node.node_type})
                </span>
                {node.description ? (
                  <span className="truncate text-text-secondary">
                    {node.description}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-foreground">
          Edges ({model.edges.length})
        </h3>
        {model.edges.length === 0 ? (
          <p className="text-sm text-text-tertiary">No edges declared.</p>
        ) : (
          <ul className="space-y-1">
            {model.edges.map((edge, idx) => (
              <li
                key={`${edge.source_id}->${edge.target_id}-${idx}`}
                className="font-mono text-sm text-text-secondary"
              >
                {edge.source_id} → {edge.target_id}
                {edge.routing_key ? (
                  <span className="text-text-tertiary"> [{edge.routing_key}]</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

// Default selection on page load: first LLM node so the inspector starts
// populated. Falls through to the inspector's empty state when none exists.
// Gate-vs-LLM distinction is M1's call (isGateNodeType).
function firstLlmNodeId(model: GraphAgentModel): string | null {
  const node = model.nodes.find((n) => !isGateNodeType(n.node_type))
  return node ? node.id : null
}

// Top-left canvas overlay (design mockup §graph overlay). Only renders when a
// node is selected; the pulsing mint dot signals the live selection.
function InspectingChip({ label }: { label: string | null }) {
  if (!label) return null
  return (
    <div className="absolute left-[14px] top-[14px] z-10 flex items-center gap-2 rounded-md border border-primary/40 bg-[rgba(7,8,10,0.85)] px-2.5 py-1.5 text-xs shadow-glow-sm">
      <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-primary shadow-[0_0_8px_#45F0C8]" />
      <span className="text-text-tertiary">Inspecting</span>
      <span className="font-mono text-primary">{label}</span>
    </div>
  )
}

// Bottom-left of the canvas (design mockup).
function GraphLegend() {
  return (
    <div className="absolute bottom-[14px] left-[14px] z-10 flex items-center gap-3.5 rounded-md border border-border bg-[rgba(7,8,10,0.8)] px-3 py-1.5 text-[11px] text-text-tertiary">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-[9px] w-[9px] rounded-[3px] border border-[#3A5C58] bg-[#0A2024]" />
        in / out
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-[9px] w-[9px] rounded-[3px] border border-border-active bg-[#0B2A30]" />
        LLM
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-[9px] w-[9px] rounded-[3px] border border-lineage bg-[#0B2A30]" />
        gate
      </span>
    </div>
  )
}

export function GraphCard({ model }: { model: GraphAgentModel }) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(() =>
    firstLlmNodeId(model),
  )
  const [renderFailed, setRenderFailed] = useState(false)

  // M1: pure text generation from the wire-shape nodes/edges. Cheap and
  // synchronous; recomputed only when the model identity changes.
  const definition = generateMermaidFlowchart(model.nodes, model.edges)

  // Resolve the page-level selection. It is either a concrete node, one
  // of the synthetic `input` / `output` endpoints (which surface the
  // agent-level I/O schema in the inspector), or `null`.
  const selectedNode =
    model.nodes.find((node) => node.id === selectedNodeId) ?? null
  const selectedEndpoint = selectedNode ? null : endpointOf(selectedNodeId)
  const inspectingLabel = selectedNode?.name ?? selectedEndpoint ?? null

  return (
    <div className="grid grid-cols-1 items-stretch gap-3.5 lg:grid-cols-[minmax(0,2fr)_minmax(min(100%,23em),1fr)]">
      <section
        aria-label="Graph agent model"
        className="relative box-dots min-h-[640px]"
      >
        {/* Mint hero glow seeping from the top-right corner (mockup).
            Rounded to the card so it never shows square corners now that
            the section no longer clips its children. */}
        <div className="pointer-events-none absolute inset-0 rounded-lg bg-[radial-gradient(600px_320px_at_70%_-10%,rgba(69,240,200,0.08),transparent_60%)]" />

        {renderFailed ? (
          <div className="relative h-full overflow-auto rounded-lg p-5">
            <GraphTextLists model={model} />
          </div>
        ) : (
          <>
            {/* "Inspecting" chip — top-left, shows the selected node
                name. Only over the live diagram and only when a node is
                selected; the degraded text-list branch has no diagram. */}
            <InspectingChip label={inspectingLabel} />

            {/* Node-type legend — bottom-left. */}
            <GraphLegend />

            {/* The diagram is inset from the canvas edges so node chips
                never touch the chrome. The section is deliberately NOT
                `overflow-hidden`: the node selection/hover neon glow
                extends past the diagram bounds and would otherwise be
                shorn ("box du graphe cropé"). */}
            <div className="absolute inset-[30px]">
              <MermaidDiagram
                definition={definition}
                nodes={model.nodes}
                selectedNodeId={selectedNodeId}
                onNodeClick={setSelectedNodeId}
                onError={() => setRenderFailed(true)}
              />
            </div>
          </>
        )}
      </section>

      <NodeInspector
        node={selectedNode}
        endpoint={selectedEndpoint}
        model={model}
      />
    </div>
  )
}
